import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const ROOT = {
  name: "François Dossa TANDJIEKPON LANWASSONOU",
  detail: "Le Patriarche — 1938 – 2026. Racine et mémoire de la famille.",
};

const TONES = {
  wife: "border-[#E8D489] bg-[#FDF3C8]",
  child: "border-[#B9C8E4] bg-[#E8EEF7]",
};

const WIVES = [
  {
    name: "Vitchoédo Noudègbessi Houantoungbé Matha",
    deceased: true,
    tone: "blue",
    children: [
      { name: "Mahoutin André" },
      { name: "Sourou" },
      { name: "Kpèdétin Eugenie" },
      { name: "Chadjou Lucien" },
      { name: "Ouansivo Christine" },
      { name: "Etienne" },
      { name: "Frédéric", deceased: true },
      { name: "Hounsinou Laurent" },
    ],
  },
  {
    name: "Adanmitondé Akpomè Marie Madéleine",
    deceased: true,
    tone: "yellow",
    children: [{ name: "Yves" }, { name: "Fifadji Pélagie" }],
  },
  {
    name: "Koussouali Okou Eriyomi",
    tone: "blue",
    children: [{ name: "Bernard", deceased: true }, { name: "Flora" }],
  },
  {
    name: "Kpovianou Lalivou Missihoun",
    tone: "yellow",
    children: [
      { name: "Mondukpè Hélène" },
      { name: "Barthélemy" },
      { name: "Houévo Thérèse" },
      { name: "Gbosséyon Patrice" },
      { name: "Ekpoyessi Firmin" },
      { name: "Sègbégnon Angelo" },
      { name: "Sènami Rosine" },
    ],
  },
  {
    name: "Dèdèwanou Yétondé Bernadette",
    tone: "blue",
    children: [
      { name: "Chédji Solange" },
      { name: "Dagbégnon Victoire" },
      { name: "Yénoukoumè Clémance" },
      { name: "Fifadji Célestine" },
      { name: "Enangnon Marc" },
      { name: "Yédénou Salomé" },
      { name: "Abègnonhou Véronique" },
    ],
  },
  {
    name: "Dohou Mahoussi Thérèse",
    tone: "yellow",
    children: [
      { name: "Sisingbé Julienne" },
      { name: "Yèmessi Alice" },
      { name: "Abodourin Suzane" },
      { name: "Homagnissi Emilienne" },
      { name: "Monnèdjo Eric" },
      { name: "Monloto Viviane" },
    ],
  },
  {
    name: "Ouinsavi Christine Mèlanie",
    deceased: true,
    tone: "blue",
    children: [
      { name: "Ahignon Guilenne" },
      { name: "Noutin Lucianord" },
      { name: "Sènoumè Glawdys" },
    ],
  },
];

const TOTAL_CHILDREN = WIVES.reduce((n, w) => n + w.children.length, 0);

export default function Tree() {
  const [branch, setBranch] = useState(0);
  const [selected, setSelected] = useState(ROOT);
  const wife = WIVES[branch];

  const selectWife = (i) => {
    const w = WIVES[i];
    setBranch(i);
    setSelected({
      name: w.name,
      detail: `Épouse du Patriarche — ${w.children.length} enfant${w.children.length > 1 ? "s" : ""}${w.deceased ? " • De mémoire ✝" : ""}`,
    });
  };

  return (
    <section id="famille" data-testid="family-tree-section" className="px-4 py-16 sm:py-24">
      <div className="mx-auto max-w-5xl">
        <Reveal className="mb-12 text-center">
          <p className="flex items-center justify-center gap-2 text-[11px] font-semibold uppercase tracking-[0.3em] text-[#C5A059]">
            <Users className="h-4 w-4" /> L'héritage
          </p>
          <h2 className="mt-3 font-display text-3xl font-semibold text-stone-900 sm:text-4xl">
            Arbre Généalogique
          </h2>
          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-stone-500 sm:text-base">
            Sept branches issues du grand arbre — touchez une épouse pour
            découvrir sa descendance.
          </p>
          <p className="mx-auto mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px] text-stone-400">
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-3 w-3 rounded-sm border border-[#E8D489] bg-[#FDF3C8]" />
              Épouses
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-3 w-3 rounded-sm border border-[#B9C8E4] bg-[#E8EEF7]" />
              Enfants
            </span>
            <span className="flex items-center gap-1">
              <span className="text-sm font-bold text-stone-500">✝</span> De mémoire
            </span>
          </p>
        </Reveal>

        <Reveal>
          <div className="flex flex-col items-center">
            <button
              data-testid="tree-root"
              onClick={() => setSelected(ROOT)}
              className="w-full max-w-xs rounded-2xl border border-[#C5A059]/40 bg-white px-6 py-4 text-center shadow-sm transition-colors duration-300 hover:border-[#C5A059]"
            >
              <img
                src="/images/portrait.jpg"
                alt="Le Patriarche"
                className="mx-auto h-16 w-16 rounded-full border-2 border-[#C5A059] object-cover object-top"
              />
              <p className="mt-2 font-display text-base font-semibold leading-tight text-stone-900">
                {ROOT.name}
              </p>
              <p className="text-[11px] text-[#C85A32]">
                Le Patriarche • 1938 – 2026
              </p>
              <p className="mt-1 text-[11px] text-stone-400">
                7 épouses • {TOTAL_CHILDREN} enfants
              </p>
            </button>

            <div className="h-8 w-px bg-[#C5A059]/50" />

            <div className="w-full">
              <div
                data-testid="tree-wives"
                className="flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2 [scrollbar-width:none] sm:grid sm:grid-cols-4 sm:overflow-visible lg:grid-cols-7 [&::-webkit-scrollbar]:hidden"
              >
                {WIVES.map((w, i) => (
                  <button
                    key={w.name}
                    data-testid={`tree-wife-${i}`}
                    onClick={() => selectWife(i)}
                    className={`min-w-[150px] snap-start rounded-2xl border px-3 py-3 text-center shadow-sm transition-colors duration-300 sm:min-w-0 ${TONES.wife} ${
                      branch === i
                        ? "border-[#C5A059] ring-2 ring-[#C5A059]/60"
                        : "hover:border-[#C5A059]"
                    }`}
                  >
                    <p className="font-display text-xs font-semibold leading-snug text-stone-800">
                      {w.name}
                      {w.deceased && (
                        <span className="ml-1 align-middle text-sm font-bold text-stone-500">
                          ✝
                        </span>
                      )}
                    </p>
                    <p className="mt-1 text-[10px] text-stone-500">
                      {w.children.length} enfant{w.children.length > 1 ? "s" : ""}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={branch}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="mt-6 w-full"
              >
                <p className="mb-4 text-center text-[11px] font-semibold uppercase tracking-[0.25em] text-[#C85A32]">
                  Branche de {wife.name}
                </p>
                {wife.children.length > 0 ? (
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 lg:grid-cols-5">
                    {wife.children.map((c, ci) => (
                      <button
                        key={c.name}
                        data-testid={`tree-child-${branch}-${ci}`}
                        onClick={() =>
                          setSelected({
                            name: c.name,
                            detail: `Enfant du Patriarche et de ${wife.name}${c.deceased ? " • De mémoire ✝" : ""}`,
                          })
                        }
                        className={`rounded-xl border px-3 py-2.5 text-center transition-colors duration-300 ${
                          c.deceased
                            ? "border-stone-300 bg-stone-200/70 text-stone-500"
                            : `${TONES.child} text-stone-700 hover:border-[#C5A059] hover:text-stone-900`
                        }`}
                      >
                        <p className="font-display text-sm font-medium leading-snug">
                          {c.name}
                          {c.deceased && (
                            <span className="ml-1 align-middle text-base font-bold text-stone-600">
                              ✝
                            </span>
                          )}
                        </p>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-sm italic text-stone-400">
                    Aucun enfant recensé dans cette branche.
                  </p>
                )}
              </motion.div>
            </AnimatePresence>

            <AnimatePresence mode="wait">
              {selected && (
                <motion.div
                  key={selected.name}
                  data-testid="tree-detail"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.3 }}
                  className="mt-8 w-full max-w-md rounded-2xl border border-[#C5A059]/30 bg-white p-5 text-center shadow-sm"
                >
                  <p className="font-display text-lg font-semibold text-stone-900">
                    {selected.name}
                  </p>
                  <p className="mt-1 text-sm text-stone-500">{selected.detail}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
